/**
 * =============================================================================
 * CONFIGURAÇÃO DE SEO
 * =============================================================================
 *
 * Centraliza todos os metadados do site: títulos, descrições, Open Graph,
 * Twitter Cards, keywords e Structured Data (Schema.org).
 *
 * Regra de escrita: Sentence case — apenas a primeira palavra começa com
 * maiúscula, exceto nomes próprios, marcas e siglas.
 */

export const SITE_URL = "https://oficinadamente.top";
export const SITE_NAME = "Oficina da Mente";
export const DEFAULT_OG_IMAGE = "/pedro-profile.jpg";
export const LOCALE = "pt_PT";

export const CONTACT = {
  phone: "+351962689201",
  phoneDisplay: "96 26 89 201",
  email: "oficinadamente2016@gmail.com",
  address: {
    street: "Travessa do Ferro nº 7",
    locality: "Moure de Madalena",
    postalCode: "3515-336",
    region: "Viseu",
    country: "PT",
  },
};

export interface PageSeo {
  title: string;
  description: string;
  ogType?: "website" | "article" | "book" | "profile";
  ogImage?: string;
  keywords?: string;
  noindex?: boolean;
  schema?: SchemaValue | SchemaValue[];
}

type SchemaValue = Record<string, unknown>;

/**
 * ---------------------------------------------------------------------------
 * Metadados por página
 * ---------------------------------------------------------------------------
 */
export const pageSeoMap: Record<string, PageSeo> = {
  "/": {
    title: "Oficina da Mente — Pedro de Freitas",
    description:
      "Hipnoterapia, formação, super aprendizagem e bem-estar mental com Pedro de Freitas em Viseu. Descubra ferramentas para uma vida mais equilibrada.",
    ogType: "website",
    keywords:
      "hipnoterapia, formação, super aprendizagem, bem-estar mental, Viseu, Pedro de Freitas",
    schema: [websiteSchema(), localBusinessSchema(), personSchema()],
  },
  "/curriculo": {
    title: "Currículo de Pedro de Freitas — Oficina da Mente",
    description:
      "Percursos académico e profissional de Pedro de Freitas: hipnoterapia, formação, educação e desenvolvimento pessoal.",
    ogType: "profile",
    keywords: "currículo, Pedro de Freitas, hipnoterapia, formação, educação",
    schema: [personSchema()],
  },
  "/hipnoterapia": {
    title: "Hipnoterapia em Viseu — Oficina da Mente",
    description:
      "Sessões de hipnoterapia em Viseu com Pedro de Freitas. Apoio para ansiedade, stress, hábitos e desenvolvimento pessoal.",
    ogType: "article",
    keywords: "hipnoterapia, Viseu, ansiedade, stress, hábitos, desenvolvimento pessoal",
    schema: [serviceSchema("Hipnoterapia")],
  },
  "/super-aprendizagem": {
    title: "Super aprendizagem escolar — Oficina da Mente",
    description:
      "Método de super aprendizagem para crianças e jovens: melhore a concentração, a memória e os resultados escolares.",
    ogType: "article",
    keywords: "super aprendizagem, estudo, concentração, memória, crianças, jovens",
    schema: [serviceSchema("Super aprendizagem")],
  },
  "/formacao": {
    title: "Formação e workshops — Oficina da Mente",
    description:
      "Workshops e formações de hipnoterapia, mindfulness e desenvolvimento pessoal com Pedro de Freitas.",
    ogType: "article",
    keywords: "formação, workshops, mindfulness, desenvolvimento pessoal, Viseu",
    schema: [serviceSchema("Formação")],
  },
  "/pro-bono": {
    title: "Sessões pro bono em escolas — Oficina da Mente",
    description:
      "Sessões pro bono de hipnoterapia e bem-estar para comunidades escolares. Saiba como solicitar uma sessão.",
    ogType: "article",
    keywords: "pro bono, escolas, hipnoterapia, bem-estar, comunidade educativa",
    schema: [serviceSchema("Pro bono")],
  },
  "/psicologia": {
    title: "Consultas de psicologia — Oficina da Mente",
    description:
      "Um espaço de escuta, compreensão e desenvolvimento. Psicologia para crianças e adolescentes e orientação vocacional na Oficina da Mente, em Viseu.",
    ogType: "article",
    keywords: "psicologia, consultas psicológicas, psicologia infantil e adolescentes, orientação vocacional, Viseu, bem-estar emocional",
    schema: [serviceSchema("Psicologia")],
  },
  "/livros/manual-para-professores": {
    title: "Manual para Professores — Oficina da Mente",
    description:
      "Livro Manual para Professores de Pedro de Freitas: estratégias práticas para o bem-estar e o sucesso na sala de aula.",
    ogType: "book",
    ogImage: "/book-manual-cover.jpg",
    keywords: "livro, professores, educação, bem-estar, sala de aula, Pedro de Freitas",
    schema: [bookSchema("Manual para Professores", "/book-manual-cover.jpg")],
  },
  "/livros/ser-maior": {
    title: "Ser Maior — Oficina da Mente",
    description:
      "Livro Ser Maior e meditações guiadas de Pedro de Freitas. Ferramentas para crianças e jovens crescerem com confiança.",
    ogType: "book",
    ogImage: "/book-ser-maior-cover.jpg",
    keywords: "livro, Ser Maior, meditações, crianças, jovens, confiança",
    schema: [bookSchema("Ser Maior", "/book-ser-maior-cover.jpg")],
  },
  "/privacidade": {
    title: "Política de privacidade — Oficina da Mente",
    description:
      "Política de privacidade do site Oficina da Mente: dados recolhidos, finalidades, conservação, cookies e os teus direitos ao abrigo do RGPD.",
    ogType: "article",
    keywords: "política de privacidade, RGPD, dados pessoais, cookies, CNPD",
  },
  "/termos-e-condicoes": {
    title: "Termos e condições — Oficina da Mente",
    description:
      "Termos e condições de utilização do site Oficina da Mente: serviços, encomendas de livros, propriedade intelectual e responsabilidade.",
    ogType: "article",
    keywords: "termos e condições, condições de utilização, Oficina da Mente",
  },
};

/**
 * ---------------------------------------------------------------------------
 * Helpers
 * ---------------------------------------------------------------------------
 */
export function getPageSeo(path: string): PageSeo {
  const normalized = path.replace(/\/$/, "") || "/";
  return pageSeoMap[normalized] ?? pageSeoMap["/"];
}

export function buildCanonicalUrl(path: string): string {
  const cleanPath = path === "/" ? "" : path.replace(/\/$/, "");
  return `${SITE_URL}${cleanPath}`;
}

/**
 * ---------------------------------------------------------------------------
 * Schema.org — Structured Data
 * ---------------------------------------------------------------------------
 */
export function websiteSchema(): SchemaValue {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SITE_NAME,
    url: SITE_URL,
    inLanguage: "pt-PT",
    potentialAction: {
      "@type": "SearchAction",
      target: `${SITE_URL}/?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  };
}

export function personSchema(): SchemaValue {
  return {
    "@context": "https://schema.org",
    "@type": "Person",
    name: "Pedro de Freitas",
    jobTitle: "Hipnoterapeuta e Formador",
    url: SITE_URL,
    image: `${SITE_URL}/pedro-profile.jpg`,
    worksFor: {
      "@type": "Organization",
      name: SITE_NAME,
      url: SITE_URL,
    },
    sameAs: [
      "https://www.oficinadamente.top",
    ],
  };
}

export function localBusinessSchema(): SchemaValue {
  return {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    name: SITE_NAME,
    url: SITE_URL,
    telephone: CONTACT.phone,
    email: CONTACT.email,
    priceRange: "€€",
    areaServed: {
      "@type": "City",
      name: "Viseu",
      containedInPlace: {
        "@type": "Country",
        name: "Portugal",
      },
    },
    address: {
      "@type": "PostalAddress",
      streetAddress: CONTACT.address.street,
      addressLocality: CONTACT.address.locality,
      postalCode: CONTACT.address.postalCode,
      addressRegion: CONTACT.address.region,
      addressCountry: CONTACT.address.country,
    },
  };
}

export function serviceSchema(serviceName: string): SchemaValue {
  const descriptions: Record<string, string> = {
    Hipnoterapia:
      "Sessões de hipnoterapia para ansiedade, stress, hábitos e desenvolvimento pessoal.",
    "Super aprendizagem":
      "Método de super aprendizagem para melhorar a concentração, a memória e os resultados escolares.",
    Formação:
      "Workshops e formações de hipnoterapia, mindfulness e desenvolvimento pessoal.",
    "Pro bono":
      "Sessões pro bono de hipnoterapia e bem-estar para comunidades escolares.",
    Psicologia:
      "Acompanhamento psicológico individual, confidencial e ajustado às necessidades de cada pessoa.",
  };

  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name: serviceName,
    description: descriptions[serviceName] ?? `Serviço ${serviceName} da ${SITE_NAME}.`,
    provider: {
      "@type": "ProfessionalService",
      name: SITE_NAME,
      url: SITE_URL,
    },
    areaServed: {
      "@type": "City",
      name: "Viseu",
      containedInPlace: {
        "@type": "Country",
        name: "Portugal",
      },
    },
  };
}

export function bookSchema(title: string, coverImage: string): SchemaValue {
  return {
    "@context": "https://schema.org",
    "@type": "Book",
    name: title,
    author: {
      "@type": "Person",
      name: "Pedro de Freitas",
      url: SITE_URL,
    },
    image: `${SITE_URL}${coverImage}`,
    url: `${SITE_URL}/livros/${title.toLowerCase().replace(/\s+/g, "-")}`,
    inLanguage: "pt-PT",
  };
}
